import React from "react";
import { motion } from "framer-motion";
import { Check, Clock, ShieldCheck, Truck, PhoneCall } from "lucide-react";
import { trackEvent, trackConversion } from "../../utils/gtag";

/**
 * Price reveal card. Two display modes:
 *   • Default       — shows "I'm Interested · Call Me Back" CTA (legacy fallback)
 *   • readOnly=true — post lead capture, shows "Already submitted · We'll call
 *                     you back" passive confirmation. The lead is already in
 *                     CRM at this point — price card is just the reward.
 */
const CarServicePriceCard = ({ price, vehicle, onBookNow, onReset, readOnly = false }) => {
  const handleBook = () => {
    trackEvent("book_now_clicked", {
      vehicle: vehicle?.display,
      price: price?.amount_display,
    });
    trackConversion("book_now_clicked", { value: price?.amount_display });
    onBookNow?.();
  };

  return (
    <motion.div
      initial={{ scale: 0.96, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="bg-white border border-slate-200 rounded-2xl shadow-[0_8px_32px_-4px_rgba(15,23,42,0.1)] overflow-hidden"
      data-testid="price-reveal-card"
    >
      {/* Vehicle bar */}
      <div className="px-5 sm:px-6 py-4 bg-slate-900 text-white flex items-center justify-between">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-0.5">
            Your Car
          </div>
          <div className="text-base sm:text-lg font-bold">
            {vehicle?.display || "Selected vehicle"}
          </div>
        </div>
        <button
          onClick={onReset}
          className="text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-white transition-colors px-3 py-1.5 border border-slate-700 hover:border-slate-500 rounded-sm"
          data-testid="reset-lookup-btn"
        >
          Change
        </button>
      </div>

      {/* Body */}
      <div className="p-5 sm:p-7">
        <div className="flex items-baseline gap-3 mb-1">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">
            General Service
          </span>
        </div>
        <div className="flex items-baseline gap-2 mb-5">
          <span className="font-mono text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
            ₹{(price?.amount_display ?? 0).toLocaleString("en-IN")}
          </span>
          <span className="text-sm text-slate-500 font-medium">all-inclusive</span>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6 pb-6 border-b border-slate-200">
          <Stat icon={Clock}        label="DURATION"  value={`~${price?.duration_minutes || 90} min`} />
          <Stat icon={Truck}        label="DOORSTEP"  value="Free" />
          <Stat icon={ShieldCheck}  label="WARRANTY"  value={price?.warranty || "1,000 km"} />
        </div>

        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">
            What's Included
          </div>
          <ul className="space-y-2">
            {(price?.inclusions || []).map((inc, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>{inc.label}</span>
              </li>
            ))}
          </ul>
        </div>

        {readOnly ? (
          <div
            className="w-full flex items-center gap-3 px-5 py-4 rounded-xl bg-green-50 border border-green-200"
            data-testid="lead-captured-confirmation"
          >
            <div className="w-10 h-10 rounded-full bg-green-600 inline-flex items-center justify-center shrink-0">
              <PhoneCall className="h-5 w-5 text-white" strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-extrabold text-slate-900 leading-tight">
                Already submitted · We'll call you back
              </div>
              <div className="text-xs text-green-700 mt-0.5 font-semibold">
                Our team will reach you in ~30 minutes to confirm.
              </div>
            </div>
          </div>
        ) : (
          <>
            <button
              onClick={handleBook}
              data-testid="book-now-cta"
              className="w-full inline-flex items-center justify-center px-6 py-4 rounded-xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 transition-all text-white font-extrabold tracking-tight text-base sm:text-lg shadow-[0_8px_24px_-6px_rgba(37,99,235,0.5)] hover:shadow-[0_12px_28px_-6px_rgba(37,99,235,0.6)] hover:-translate-y-[2px]"
            >
              I'm Interested · Call Me Back
            </button>
            <p className="text-xs text-center text-slate-500 mt-3">
              <ShieldCheck className="inline h-3.5 w-3.5 text-green-600 mr-1 -mt-0.5" />
              Service in 1 Hr, at your doorstep · 1,000 km Warranty FREE
            </p>
          </>
        )}
      </div>
    </motion.div>
  );
};

const Stat = ({ icon: Icon, label, value }) => (
  <div>
    <Icon className="h-4 w-4 text-slate-400 mb-1.5" />
    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
      {label}
    </div>
    <div className="text-sm font-bold text-slate-900 mt-0.5">{value}</div>
  </div>
);

export default CarServicePriceCard;
